import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import ResolvePopup from 'components/ResolvePopup';
import DownloadIcon from 'icons/Download.icon';
import EyeIcon from 'icons/Eye.icon';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import useDataRoomDocument from 'hooks/useDataRoomDocument';
import { useTranslation } from 'react-i18next';
import useNumberOfMembersWithAccess from 'hooks/useNumberOfMembersWithAccess';
import UsersWithAccessModal from '../UsersWithAccessModal';
import useCallbackRef from 'hooks/useCallbackRef';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';

import './DesktopMemberAccess.styles.scss';
import './DesktopMemberAccess.styles.responsive.scss';

type DesktopMemberAccessProps = {
  className?: string;
  node: DataRoomNode;
  isAdmin: boolean;
  isBuySide: boolean;
  decrementNumberOfMemberAccess: () => void;
};

const DesktopMemberAccess: React.FC<DesktopMemberAccessProps> = (props) => {
  const { className, isBuySide, isAdmin, node, decrementNumberOfMemberAccess } =
    props;

  const { canDownload } = useDataRoomDocument(node);

  const { _id, dataRoomId, numberOfMembersWithAccess } = node || {};

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  const numberOfMembersWithAccessCopy = useNumberOfMembersWithAccess()(
    numberOfMembersWithAccess,
  );

  const classes = classNames('desktop-member-access', className);

  const classesClickable = classNames(classes, 'clickable');

  const { t } = useTranslation();

  const hasMembersWithAccess = useMemo(
    () => numberOfMembersWithAccess > 0,
    [numberOfMembersWithAccess],
  );

  const onClick = useCallback(() => {
    if (hasMembersWithAccess) {
      modal?.open();
    }
  }, [hasMembersWithAccess, modal]);

  if (isBuySide) {
    return (
      <div className={classes}>
        <ResolvePopup
          onHover
          popupContent={t(canDownload ? 'download' : 'view')}
        >
          {canDownload ? <DownloadIcon /> : <EyeIcon />}
        </ResolvePopup>
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className={classesClickable} onClick={onClick}>
      {numberOfMembersWithAccessCopy}

      {hasMembersWithAccess && (
        <UsersWithAccessModal
          ref={modalRef}
          nodeId={_id}
          dataRoomId={dataRoomId}
          decrementNumberOfMemberAccess={decrementNumberOfMemberAccess}
        />
      )}
    </div>
  );
};

export default DesktopMemberAccess;
